import { Paper } from "@mui/material";
import { KaminCriterionPoint, KaminMonitoringRow } from "../../Requests/kamin2leg";
import { printPriceWithDecimalsForTableCell } from "../../helper";

const number = (value?: number | null) => value == null || !Number.isFinite(value) ? "—" : printPriceWithDecimalsForTableCell(value);
const cardScale = 0.7;
const cardWidth = 170 * cardScale;
const isOpenPoint = (key: string) => key === "open" || key.startsWith("open-");
const colors: Record<string, string> = { final: "#d97706", lower: "#0284c7", floor: "#15803d", ceiling: "#22c55e",
  upper: "#0369a1", competitor: "#7c3aed", ideal: "#9333ea", selected: "#be123c", open: "#334155", top: "#0891b2" };

// Legacy records retain their recorded boundaries; missing historical volumes remain empty.
function legacyPoints(row: KaminMonitoringRow): KaminCriterionPoint[] {
  return [
    { key: "final", name: "مرز نهایی رفع ناترازی", price: row.finalHedgePrice, criterion: row.finalHedgeMaxCost },
    { key: "lower", name: "کف عدم حساسیت", price: row.lowerSensitivityPrice, criterion: row.lowerSensitivityCriterion },
    { key: "floor", name: "کف هدف", price: row.targetLowerPrice, criterion: row.targetLower,
      volumeAhead: row.targetLowerPrice === row.floorKaminPrice ? row.volumeAheadOfFloor as number | null : null },
    { key: "ceiling", name: "سقف هدف", price: row.targetUpperPrice, criterion: row.targetUpper },
    { key: "upper", name: "سقف عدم حساسیت", price: row.upperSensitivityPrice, criterion: row.upperSensitivityCriterion },
  ];
}

export default function KaminCycleAxis({ row }: { row: KaminMonitoringRow }) {
  const points = row.criterionPoints?.length ? row.criterionPoints : legacyPoints(row);
  const valid = points.filter(point => point.criterion != null && Number.isFinite(point.criterion));
  const grouped: { key: string; price?: number | null; criterion: number; volumeAhead?: number | null; points: KaminCriterionPoint[] }[] = [];
  for (const point of valid) {
    const group = point.price != null && Number.isFinite(point.price)
      ? grouped.find(item => item.price === point.price && item.criterion === point.criterion) : undefined;
    if (group) {
      group.points.push(point);
      group.volumeAhead ??= point.volumeAhead;
    } else grouped.push({ key: point.key, price: point.price, criterion: point.criterion!, volumeAhead: point.volumeAhead, points: [point] });
  }
  const min = valid.length ? Math.min(...valid.map(point => point.criterion!)) : 0;
  const max = valid.length ? Math.max(...valid.map(point => point.criterion!)) : 0;
  const higherIsBetter = row.criterionHigherIsBetter ?? row.criterionType !== "SignedCost";
  const position = (value: number) => max === min ? 500 : higherIsBetter
    ? 950 - (value - min) * 900 / (max - min) : 50 + (value - min) * 900 / (max - min);
  const lanes: { end: number; height: number }[] = [];
  const layout = grouped.map(point => ({ ...point, x: position(point.criterion) })).sort((a, b) => a.x - b.x)
    .map(point => {
      const left = Math.max(8, Math.min(992 - cardWidth, point.x - cardWidth / 2));
      let lane = lanes.findIndex(item => left >= item.end + 10);
      const headerHeight = 17 + (point.points.length - 1) * 15;
      const height = (headerHeight + 71) * cardScale;
      if (lane < 0) { lane = lanes.length; lanes.push({ end: left + cardWidth, height }); }
      else { lanes[lane].end = left + cardWidth; lanes[lane].height = Math.max(lanes[lane].height, height); }
      return { ...point, left, lane, height, headerHeight,
        color: colors[point.points.some(item => item.key === "selected") ? "selected" : point.key] || "#475569" };
    });
  const laneOffsets = lanes.map((_, index) => lanes.slice(0, index).reduce((sum, lane) => sum + lane.height + 12, 0));
  const placed = layout.map(point => ({ ...point, y: 65 + laneOffsets[point.lane] }));
  const height = 75 + lanes.reduce((sum, lane) => sum + lane.height + 12, 0);
  return <Paper variant="outlined" className="kamin-cycle-axis-section" dir="rtl">
    <div className="kamin-cycle-section-heading">محور مقدار معیار · {row.criterionType === "SignedCost" ? "بهای تمام‌شده" : row.criterionType}</div>
    <div className="text-slate-500 mb-3">{higherIsBetter ? "مقدار بیشتر مطلوب‌تر است" : "مقدار کمتر مطلوب‌تر است"} · حجم جلو فقط حجم رقبا در قیمت‌های بهتر از قیمت نقطه است؛ حجم همان قیمت حساب نمی‌شود.</div>
    {valid.length > 0 ? <div className="kamin-cycle-axis-scroll">
      <svg viewBox={`0 0 1000 ${height}`} className="kamin-cycle-detail-axis" role="img"
        aria-label="نقاط بر محور مقدار معیار، همراه نام، قیمت، مقدار معیار و حجم جلو اردربوک">
        <text x="50" y="16" direction="ltr" textAnchor="middle" fontSize="11" fill="#64748b">{number(higherIsBetter ? max : min)}</text>
        <text x="950" y="16" direction="ltr" textAnchor="middle" fontSize="11" fill="#64748b">{number(higherIsBetter ? min : max)}</text>
        <line x1="50" x2="950" y1="35" y2="35" stroke="#94a3b8" strokeWidth="2" />
        {row.targetLower != null && row.targetUpper != null && <rect
          x={Math.min(position(row.targetLower), position(row.targetUpper))} y="25"
          width={Math.abs(position(row.targetLower) - position(row.targetUpper))} height="20" fill="#dcfce7" stroke="#15803d" />}
        {placed.map(point => <g key={`stem-${point.key}`}>
          <path d={`M ${point.x} 35 V ${point.y - 12} H ${point.left + cardWidth / 2} V ${point.y}`} fill="none" stroke={point.color} strokeOpacity=".55" />
          <circle cx={point.x} cy="35" r="5" fill={point.color} />
        </g>)}
        {placed.map(point => <g key={point.key} transform={`translate(${point.left} ${point.y}) scale(${cardScale})`}>
          <title>{`${point.points.map(item => item.name).join("، ")}؛ قیمت ${number(point.price)}؛ معیار ${number(point.criterion)}؛ حجم جلو ${number(point.volumeAhead)}`}</title>
          <rect x="0" y="0" width="170" height={point.height / cardScale} rx="4" fill="white" stroke={point.color} />
          {point.points.map((item, index) => <text key={item.key} x="85"
            y={17 + index * 15} textAnchor="middle" direction="rtl" fontSize="10"
            fontWeight={isOpenPoint(item.key) ? 700 : 400}
            fill={isOpenPoint(item.key) ? "#0f172a" : colors[item.key] || "#475569"}>{item.name}</text>)}
          {([["قیمت", point.price], ["معیار", point.criterion], ["حجم جلو", point.volumeAhead]] as const).map(([label, value], index) => <g key={label}>
            <text x="158" y={point.headerHeight + 20 + index * 18} textAnchor="start" direction="rtl" fontSize="11" fill="#475569">{label}</text>
            <text x="10" y={point.headerHeight + 20 + index * 18} textAnchor="start" direction="ltr" fontSize="11" fontWeight="600" fill="#0f172a">{number(value)}</text>
          </g>)}
        </g>)}
      </svg>
    </div> : <div className="py-3 text-slate-500">مقدار معیار برای رسم محور در این رکورد موجود نیست.</div>}
    {!row.criterionPoints?.length && <div className="mt-2 text-slate-500">حجم جلو سایر نقاط در این رکورد قدیمی ذخیره نشده است.</div>}
  </Paper>;
}
